import type { ChatMessage } from '@/stores/useAiAssistantStore';
import { isEngineeringTableAxisLabel } from '@/utils/aiPageContentHighlight';
import { isCrossBlockQuote } from '@/utils/htmlTextAnchor';

export type AiPageContentUserRefPayload = {
  fieldLabel: string;
  quoted: string;
  requirement: string;
  scopeLabel?: string;
  fieldPath?: string;
  cellKey?: string;
  paragraphIndex?: number;
  paragraphLabel?: string;
};

export type AiPageContentFieldEditPayload = {
  fieldPath: string;
  original: string;
  revised: string;
  status: 'pending' | 'accepted' | 'rejected';
  cellKey?: string;
  paragraphIndex?: number;
  paragraphLabel?: string;
  quoted?: string;
  /** 面向用户的说明文字，用于 content 缺失时的展示与恢复 */
  replyText?: string;
};

export function getAiPageContentUserRefFromMessage(msg: ChatMessage): AiPageContentUserRefPayload | null {
  const sr = msg.structuredResult;
  if (!sr || typeof sr !== 'object') return null;
  const raw = (sr as Record<string, unknown>).page_content_user_ref;
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const fieldLabel = typeof o.fieldLabel === 'string' ? o.fieldLabel : '';
  const quoted = typeof o.quoted === 'string' ? o.quoted : '';
  const requirement = typeof o.requirement === 'string' ? o.requirement : '';
  const scopeLabel = typeof o.scopeLabel === 'string' ? o.scopeLabel : '';
  const fieldPath = typeof o.fieldPath === 'string' ? o.fieldPath : '';
  const cellKey = typeof o.cellKey === 'string' && o.cellKey.trim() ? o.cellKey.trim() : undefined;
  const paragraphIndex = typeof o.paragraphIndex === 'number' ? o.paragraphIndex : undefined;
  const paragraphLabel = typeof o.paragraphLabel === 'string' && o.paragraphLabel.trim()
    ? o.paragraphLabel.trim()
    : undefined;
  if (!fieldLabel || !quoted) return null;
  return {
    fieldLabel,
    quoted,
    requirement,
    scopeLabel: scopeLabel || undefined,
    fieldPath: fieldPath || undefined,
    cellKey,
    paragraphIndex,
    paragraphLabel,
  };
}

export function getAiPageContentFieldEditFromMessage(msg: ChatMessage): AiPageContentFieldEditPayload | null {
  const sr = msg.structuredResult;
  if (!sr || typeof sr !== 'object') return null;
  const raw = (sr as Record<string, unknown>).page_content_field_edit;
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const fieldPath = typeof o.fieldPath === 'string' ? o.fieldPath : '';
  const original = typeof o.original === 'string' ? o.original : '';
  const revised = typeof o.revised === 'string' ? o.revised : '';
  const st = o.status;
  const status: AiPageContentFieldEditPayload['status'] =
    st === 'accepted' || st === 'rejected' || st === 'pending' ? st : 'pending';
  const cellKey = typeof o.cellKey === 'string' && o.cellKey.trim() ? o.cellKey.trim() : undefined;
  const paragraphIndex = typeof o.paragraphIndex === 'number' ? o.paragraphIndex : undefined;
  const paragraphLabel = typeof o.paragraphLabel === 'string' && o.paragraphLabel.trim()
    ? o.paragraphLabel.trim()
    : undefined;
  const quoted = typeof o.quoted === 'string' && o.quoted.trim() ? o.quoted.trim() : undefined;
  const replyText = typeof o.replyText === 'string' && o.replyText.trim() ? o.replyText.trim() : undefined;
  if (!fieldPath) return null;
  return {
    fieldPath,
    original,
    revised,
    status,
    cellKey,
    paragraphIndex,
    paragraphLabel,
    quoted,
    replyText,
  };
}

export function isActionableAiPageContentFieldEdit(
  payload: AiPageContentFieldEditPayload | null | undefined,
): payload is AiPageContentFieldEditPayload {
  if (!payload) return false;
  const revised = String(payload.revised ?? '').trim();
  if (!revised) return false;
  const original = String(payload.original ?? '').trim();
  const quoted = String(payload.quoted ?? '').trim();
  const anchor = original || quoted;
  if (!anchor) return false;
  return revised !== anchor;
}

export function isPendingAiPageContentFieldEditMessage(msg: ChatMessage): boolean {
  const p = getAiPageContentFieldEditFromMessage(msg);
  return Boolean(p && p.status === 'pending' && isActionableAiPageContentFieldEdit(p));
}

/** 从用户引用消息及其后续助手回复中补全段落定位信息 */
export function resolveAiPageContentLocateOptions(
  ref: AiPageContentUserRefPayload,
  sessionMessages: ChatMessage[],
  messageId?: string,
): { paragraphIndex?: number; paragraphLabel?: string; cellKey?: string } {
  let paragraphIndex = ref.paragraphIndex;
  let paragraphLabel = ref.paragraphLabel;
  const cellKey = ref.cellKey;
  if (!paragraphIndex && !paragraphLabel && ref.fieldPath === 'document-body' && messageId) {
    const idx = sessionMessages.findIndex((m) => m.id === messageId);
    const nextAssistant = idx >= 0
      ? sessionMessages.slice(idx + 1).find((m) => m.role === 'assistant')
      : undefined;
    const fieldEdit = nextAssistant ? getAiPageContentFieldEditFromMessage(nextAssistant) : null;
    paragraphIndex = fieldEdit?.paragraphIndex;
    paragraphLabel = fieldEdit?.paragraphLabel;
  }
  // 工程表行/列引用：即使原文多行也必须保留 eng-row / eng-col 定位标签
  if (isEngineeringTableAxisLabel(paragraphLabel)) {
    return { paragraphIndex, paragraphLabel, cellKey };
  }
  if (isCrossBlockQuote(ref.quoted, paragraphLabel)) {
    return { cellKey };
  }
  return { paragraphIndex, paragraphLabel, cellKey };
}

export function aiPageContentFieldEditDoneLabel(msg: ChatMessage): string {
  const p = getAiPageContentFieldEditFromMessage(msg);
  if (!p || p.status === 'pending') return '';
  return p.status === 'accepted' ? '已将改写应用到页面' : '已放弃本次改写建议';
}

const PAGE_CONTENT_FIELD_EDIT_FALLBACK_REPLY =
  '已根据你的说明给出改写建议：请在页面中查看**淡蓝色背景的原文**与紧随其后的**红色改写**；满意请在本条消息末尾点击「采纳」应用到页面，否则点击「拒绝」。';

import { sanitizeFieldEditUserDisplayText } from '@/utils/projectCreateFieldEditParse';

/** 助手消息展示正文：兼容持久化时 content 未写入的情况 */
export function resolveAssistantMessageDisplayContent(msg: ChatMessage): string {
  if (msg.role !== 'assistant') return String(msg.content ?? '');
  const direct = sanitizeFieldEditUserDisplayText(String(msg.content ?? ''));
  if (direct) return direct;
  const fieldEdit = getAiPageContentFieldEditFromMessage(msg);
  if (fieldEdit?.replyText?.trim()) return sanitizeFieldEditUserDisplayText(fieldEdit.replyText);
  if (fieldEdit?.status === 'pending') return PAGE_CONTENT_FIELD_EDIT_FALLBACK_REPLY;
  return sanitizeFieldEditUserDisplayText(String(msg.content ?? ''));
}
